import { resolveAssetOpeningTimesForDisplay } from '../api/assetPayloadNormalize';
import type { AppLanguage } from '../i18n/types';
import { openingTimesNormalizeDayValue } from '../utils/openingTimesLocale';
import {
  findOpeningTimesWeekdayKey,
  OPENING_TIMES_WEEKDAY_ORDER,
  type OpeningTimesWeekday,
} from '../utils/openingTimesWeekdays';
import { normalizeVoiceText } from './matchVoiceSearchCity';

export type AssistantScheduleWeekday = OpeningTimesWeekday;

export type AssistantSchedule = {
  frequency: 'weekly' | null;
  /** Days that must be open (not closed). */
  weekdays: AssistantScheduleWeekday[];
  /** Days that must be closed (“שסגור בשבת”). */
  closed_weekdays?: AssistantScheduleWeekday[];
  /** `HH:mm` 24h */
  start_time: string | null;
  duration_minutes: number | null;
  /** Inclusive end date `YYYY-MM-DD` */
  until_date: string | null;
};

/** Wire shape on `browse` / `last_search` (snake_case, BE contract). */
export type AssistantScheduleWire = {
  frequency: 'weekly' | null;
  weekdays: string[];
  closed_weekdays?: string[];
  start_time: string | null;
  duration_minutes: number | null;
  until_date: string | null;
};

export function assistantScheduleClosedWeekdays(
  schedule: AssistantSchedule | null | undefined,
): AssistantScheduleWeekday[] {
  return schedule?.closed_weekdays ?? [];
}

const WEEKDAY_SET = new Set<string>(OPENING_TIMES_WEEKDAY_ORDER);

// Prefer “יום / בימי …” — bare “שני” matches inside “ראשון”.
const HE_WEEKDAY: Array<{ day: AssistantScheduleWeekday; phrases: string[] }> = [
  { day: 'sunday', phrases: ['יום ראשון', 'בימי ראשון', 'בכל יום ראשון'] },
  { day: 'monday', phrases: ['יום שני', 'בימי שני', 'בכל יום שני'] },
  { day: 'tuesday', phrases: ['יום שלישי', 'בימי שלישי', 'בכל יום שלישי'] },
  { day: 'wednesday', phrases: ['יום רביעי', 'בימי רביעי', 'בכל יום רביעי'] },
  { day: 'thursday', phrases: ['יום חמישי', 'בימי חמישי', 'בכל יום חמישי'] },
  { day: 'friday', phrases: ['יום שישי', 'בימי שישי', 'בכל יום שישי'] },
  { day: 'saturday', phrases: ['יום שבת', 'בימי שבת', 'בכל יום שבת'] },
];

const EN_WEEKDAY: Array<{ day: AssistantScheduleWeekday; phrases: string[] }> = [
  { day: 'sunday', phrases: ['sundays', 'sunday'] },
  { day: 'monday', phrases: ['mondays', 'monday'] },
  { day: 'tuesday', phrases: ['tuesdays', 'tuesday'] },
  { day: 'wednesday', phrases: ['wednesdays', 'wednesday'] },
  { day: 'thursday', phrases: ['thursdays', 'thursday'] },
  { day: 'friday', phrases: ['fridays', 'friday'] },
  { day: 'saturday', phrases: ['saturdays', 'saturday'] },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function trimString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function parseHm(value: string | null | undefined): string | null {
  const raw = value?.trim() ?? '';
  if (!raw) {
    return null;
  }
  if (/^\d{1,2}:\d{2}$/.test(raw)) {
    const [h, m] = raw.split(':');
    return `${String(Number(h)).padStart(2, '0')}:${m}`;
  }
  const iso = raw.match(/T(\d{2}:\d{2})/);
  return iso?.[1] ?? null;
}

function parseDate(value: unknown): string | null {
  const raw = trimString(value);
  if (!raw) {
    return null;
  }
  const day = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return day?.[1] ?? null;
}

function parseDurationMinutes(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    if (Number.isFinite(n) && n > 0) {
      return Math.round(n);
    }
  }
  return null;
}

function parseWeekdayToken(value: unknown): AssistantScheduleWeekday | null {
  const raw = trimString(value)?.toLowerCase();
  if (!raw) {
    return null;
  }
  if (WEEKDAY_SET.has(raw)) {
    return raw as AssistantScheduleWeekday;
  }
  const aliases: Record<string, AssistantScheduleWeekday> = {
    sun: 'sunday',
    mon: 'monday',
    tue: 'tuesday',
    tues: 'tuesday',
    wed: 'wednesday',
    thu: 'thursday',
    thur: 'thursday',
    thurs: 'thursday',
    fri: 'friday',
    sat: 'saturday',
  };
  return aliases[raw] ?? null;
}

function parseWeekdays(value: unknown): AssistantScheduleWeekday[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: AssistantScheduleWeekday[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const day = parseWeekdayToken(item);
    if (!day || seen.has(day)) {
      continue;
    }
    seen.add(day);
    out.push(day);
  }
  return out;
}

export function emptyAssistantSchedule(): AssistantSchedule {
  return {
    frequency: null,
    weekdays: [],
    closed_weekdays: [],
    start_time: null,
    duration_minutes: null,
    until_date: null,
  };
}

export function isAssistantScheduleActive(
  schedule: AssistantSchedule | null | undefined,
): boolean {
  if (!schedule) {
    return false;
  }
  return Boolean(
    schedule.frequency === 'weekly' ||
      schedule.weekdays.length > 0 ||
      assistantScheduleClosedWeekdays(schedule).length > 0 ||
      schedule.start_time ||
      schedule.duration_minutes ||
      schedule.until_date,
  );
}

/** Parse `browse.schedule` / `last_search.schedule` from a turn payload. */
export function parseAssistantScheduleFromWire(value: unknown): AssistantSchedule | null {
  if (!isRecord(value)) {
    return null;
  }
  const frequencyRaw = trimString(value.frequency)?.toLowerCase() ?? null;
  const frequency = frequencyRaw === 'weekly' ? 'weekly' : null;
  const weekdays = parseWeekdays(value.weekdays ?? value.weekday);
  const closed_weekdays = parseWeekdays(
    value.closed_weekdays ?? value.closedWeekdays ?? value.closed_weekday,
  );
  const start_time =
    parseHm(trimString(value.start_time) ?? trimString(value.startTime)) ??
    parseHm(trimString(value.start));
  const duration_minutes =
    parseDurationMinutes(value.duration_minutes) ??
    parseDurationMinutes(value.durationMinutes) ??
    parseDurationMinutes(value.duration);
  const until_date =
    parseDate(value.until_date) ?? parseDate(value.untilDate) ?? parseDate(value.until);
  const closedSet = new Set(closed_weekdays);
  const schedule: AssistantSchedule = {
    frequency,
    weekdays: weekdays.filter((day) => !closedSet.has(day)),
    ...(closed_weekdays.length > 0 ? { closed_weekdays } : {}),
    start_time,
    duration_minutes,
    until_date,
  };
  return isAssistantScheduleActive(schedule) ? schedule : null;
}

export function assistantScheduleToWire(
  schedule: AssistantSchedule | null | undefined,
): AssistantScheduleWire | null {
  if (!isAssistantScheduleActive(schedule) || !schedule) {
    return null;
  }
  const closed = assistantScheduleClosedWeekdays(schedule);
  return {
    frequency: schedule.frequency,
    weekdays: [...schedule.weekdays],
    ...(closed.length > 0 ? { closed_weekdays: [...closed] } : {}),
    start_time: schedule.start_time,
    duration_minutes: schedule.duration_minutes,
    until_date: schedule.until_date,
  };
}

function parseHmToMinutes(hm: string): number | null {
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) {
    return null;
  }
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour > 23 || minute > 59) {
    return null;
  }
  return hour * 60 + minute;
}

function minutesToHm(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** End clock time from start + duration (same calendar day). */
export function assistantScheduleEndTime(schedule: AssistantSchedule): string | null {
  if (!schedule.start_time || schedule.duration_minutes == null || schedule.duration_minutes <= 0) {
    return null;
  }
  const startMin = parseHmToMinutes(schedule.start_time);
  if (startMin === null) {
    return null;
  }
  const endMin = startMin + schedule.duration_minutes;
  if (endMin > 24 * 60) {
    return null;
  }
  return minutesToHm(endMin);
}

/**
 * Client filter for assistant schedule / “open|closed on &lt;weekday&gt;”.
 * - Open weekdays only (no start time): keep listings that are not closed that day.
 * - Closed weekdays (no start time): keep listings explicitly closed that day.
 * - Weekdays + start/duration: opening window must cover start→end.
 * Missing opening_times / missing weekday key → fail closed until hydrated (weekday filters).
 */
export function assetMatchesAssistantSchedule(
  asset: Record<string, unknown>,
  schedule: AssistantSchedule | null | undefined,
): boolean {
  if (!isAssistantScheduleActive(schedule) || !schedule) {
    return true;
  }
  const openDays = schedule.weekdays;
  const closedDays = assistantScheduleClosedWeekdays(schedule);
  if (openDays.length === 0 && closedDays.length === 0) {
    return true;
  }
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  // “שפתוח/שסגור בשבת” — need real opening_times; missing snapshot fails until hydrate.
  if (!schedule.start_time) {
    if (!openingTimes) {
      return false;
    }
    for (const day of closedDays) {
      const storedKey = findOpeningTimesWeekdayKey(openingTimes, day);
      if (!storedKey) {
        return false;
      }
      const raw = openingTimes[storedKey];
      if (raw === undefined || raw === null || raw === '') {
        return false;
      }
      const normalized = openingTimesNormalizeDayValue(raw);
      if (normalized !== 'closed') {
        return false;
      }
    }
    for (const day of openDays) {
      const storedKey = findOpeningTimesWeekdayKey(openingTimes, day);
      if (!storedKey) {
        return false;
      }
      const raw = openingTimes[storedKey];
      if (raw === undefined || raw === null || raw === '') {
        return false;
      }
      const normalized = openingTimesNormalizeDayValue(raw);
      if (normalized === 'closed') {
        return false;
      }
    }
    return true;
  }

  if (!openingTimes) {
    return true;
  }

  const endHm = assistantScheduleEndTime(schedule);
  if (!endHm) {
    return true;
  }
  const startMin = parseHmToMinutes(schedule.start_time);
  const endMin = parseHmToMinutes(endHm);
  if (startMin === null || endMin === null || endMin <= startMin) {
    return true;
  }
  for (const day of openDays) {
    const storedKey = findOpeningTimesWeekdayKey(openingTimes, day);
    if (!storedKey) {
      continue;
    }
    const raw = openingTimes[storedKey];
    if (raw === undefined || raw === null || raw === '') {
      continue;
    }
    const normalized = openingTimesNormalizeDayValue(raw);
    if (normalized === 'closed') {
      return false;
    }
    const openMin = parseHmToMinutes(normalized.open);
    const closeMin = parseHmToMinutes(normalized.close);
    if (openMin === null || closeMin === null || closeMin <= openMin) {
      return false;
    }
    if (startMin < openMin || endMin > closeMin) {
      return false;
    }
  }
  return true;
}

/** True when the schedule is an open/closed-on-weekday filter (no booking slot yet). */
export function isAssistantOpenWeekdayFilter(
  schedule: AssistantSchedule | null | undefined,
): boolean {
  return Boolean(
    schedule &&
      (schedule.weekdays.length > 0 ||
        assistantScheduleClosedWeekdays(schedule).length > 0) &&
      !schedule.start_time,
  );
}

export function refineAssetsForAssistantSchedule<T extends Record<string, unknown>>(
  rows: T[],
  schedule: AssistantSchedule | null | undefined,
): T[] {
  if (!isAssistantScheduleActive(schedule)) {
    return rows;
  }
  return rows.filter((row) => assetMatchesAssistantSchedule(row, schedule));
}

/** Next local calendar day for the first weekday in the schedule (or today if already that day and time not passed — simple: next or same weekday ≥ today). */
export function nextAssistantScheduleDateKey(
  schedule: AssistantSchedule,
  now: Date = new Date(),
): string | null {
  if (schedule.weekdays.length === 0) {
    return null;
  }
  const target = schedule.weekdays[0];
  const targetIndex = OPENING_TIMES_WEEKDAY_ORDER.indexOf(target);
  if (targetIndex < 0) {
    return null;
  }
  // JS: 0=Sunday … matches OPENING_TIMES_WEEKDAY_ORDER.
  for (let offset = 0; offset < 8; offset += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    if (d.getDay() === targetIndex) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const key = `${y}-${m}-${day}`;
      if (schedule.until_date && key > schedule.until_date) {
        return null;
      }
      return key;
    }
  }
  return null;
}

const WEEKDAY_CHIP_HE: Record<AssistantScheduleWeekday, string> = {
  sunday: 'יום א׳',
  monday: 'יום ב׳',
  tuesday: 'יום ג׳',
  wednesday: 'יום ד׳',
  thursday: 'יום ה׳',
  friday: 'יום ו׳',
  saturday: 'שבת',
};

const WEEKDAY_CHIP_EN: Record<AssistantScheduleWeekday, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

function formatUntilChip(until: string, language: AppLanguage): string {
  const m = until.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    return until;
  }
  if (language === 'he') {
    return `${m[3]}.${m[2]}.${m[1]}`;
  }
  return `${m[2]}/${m[3]}/${m[1]}`;
}

/** Compact chip segment for schedule (no city/street). */
export function formatAssistantScheduleChip(
  schedule: AssistantSchedule | null | undefined,
  language: AppLanguage = 'en',
): string | null {
  if (!isAssistantScheduleActive(schedule) || !schedule) {
    return null;
  }
  const bits: string[] = [];
  const dayLabels = schedule.weekdays.map((d) =>
    language === 'he' ? WEEKDAY_CHIP_HE[d] : WEEKDAY_CHIP_EN[d],
  );
  const closedLabels = assistantScheduleClosedWeekdays(schedule).map((d) =>
    language === 'he' ? WEEKDAY_CHIP_HE[d] : WEEKDAY_CHIP_EN[d],
  );
  const endHm = assistantScheduleEndTime(schedule);
  if (dayLabels.length && schedule.start_time && endHm) {
    bits.push(`${dayLabels.join(',')} ${schedule.start_time}–${endHm}`);
  } else if (dayLabels.length && schedule.start_time) {
    bits.push(`${dayLabels.join(',')} ${schedule.start_time}`);
  } else if (dayLabels.length) {
    bits.push(dayLabels.join(','));
  } else if (schedule.start_time && endHm) {
    bits.push(`${schedule.start_time}–${endHm}`);
  } else if (schedule.start_time) {
    bits.push(schedule.start_time);
  }
  if (closedLabels.length) {
    bits.push(
      language === 'he' ? `סגור ${closedLabels.join(',')}` : `closed ${closedLabels.join(',')}`,
    );
  }
  if (schedule.frequency === 'weekly') {
    bits.push(language === 'he' ? 'שבועי' : 'weekly');
  }
  if (schedule.until_date) {
    bits.push(
      language === 'he'
        ? `עד ${formatUntilChip(schedule.until_date, language)}`
        : `until ${formatUntilChip(schedule.until_date, language)}`,
    );
  }
  return bits.length > 0 ? bits.join(' · ') : null;
}

/**
 * Lightweight local overlay when the turn omits schedule (not the source of truth).
 * Covers “every Wednesday 14:00 for 2h until end of 2026”.
 */
export function parseAssistantScheduleFromUtterance(text: string): AssistantSchedule | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  const weekdays: AssistantScheduleWeekday[] = [];
  const weekly =
    /כל\s+יום|בימי|every\s+|each\s+|weekly|recurring/i.test(hay) ||
    /בכל\s+יום/i.test(hay);
  for (const entry of [...HE_WEEKDAY, ...EN_WEEKDAY]) {
    if (entry.phrases.some((p) => hay.includes(normalizeVoiceText(p)))) {
      if (!weekdays.includes(entry.day)) {
        weekdays.push(entry.day);
      }
    }
  }
  let start_time: string | null = null;
  const heTime = hay.match(/(?:בשעה|ב־|ב-)?\s*(\d{1,2})(?::(\d{2}))?/);
  const enTime = hay.match(/\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  const timeHit = enTime || heTime;
  if (timeHit) {
    let hour = Number(timeHit[1]);
    const minute = timeHit[2] ? Number(timeHit[2]) : 0;
    const ampm = timeHit[3]?.toLowerCase();
    if (ampm === 'pm' && hour < 12) {
      hour += 12;
    }
    if (ampm === 'am' && hour === 12) {
      hour = 0;
    }
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      start_time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
  }
  let duration_minutes: number | null = null;
  const heDur = hay.match(/ל(?:שעתיים|שעתיים)|ל(\d+)\s*שעות/);
  if (/לשעתיים|שעתיים/.test(hay)) {
    duration_minutes = 120;
  } else if (heDur?.[1]) {
    duration_minutes = Number(heDur[1]) * 60;
  } else {
    const enDur = hay.match(/(?:for\s+)?(\d+)\s*hours?|for\s+two\s+hours/i);
    if (/two\s+hours/i.test(hay)) {
      duration_minutes = 120;
    } else if (enDur?.[1]) {
      duration_minutes = Number(enDur[1]) * 60;
    }
  }
  let until_date: string | null = null;
  const yearHit =
    hay.match(/סוף\s+שנת\s+(\d{4})/) ||
    hay.match(/end\s+of\s+(?:the\s+)?(?:year\s+)?(\d{4})/) ||
    hay.match(/until\s+(?:the\s+)?end\s+of\s+(?:year\s+)?(\d{4})/);
  if (yearHit?.[1]) {
    const year = Number(yearHit[1]);
    if (year >= 1900 && year <= 2100) {
      until_date = `${year}-12-31`;
    }
  }
  const frequency: 'weekly' | null =
    weekly || weekdays.length > 0 ? 'weekly' : null;
  const schedule: AssistantSchedule = {
    frequency,
    weekdays,
    closed_weekdays: [],
    start_time,
    duration_minutes:
      duration_minutes && Number.isFinite(duration_minutes) && duration_minutes > 0
        ? duration_minutes
        : null,
    until_date,
  };
  return isAssistantScheduleActive(schedule) ? schedule : null;
}

/** Prefer turn schedule; fill gaps from local utterance overlay. */
export function mergeAssistantScheduleOverlay(
  primary: AssistantSchedule | null | undefined,
  overlay: AssistantSchedule | null | undefined,
): AssistantSchedule | null {
  if (!isAssistantScheduleActive(primary) && !isAssistantScheduleActive(overlay)) {
    return null;
  }
  const a = primary ?? emptyAssistantSchedule();
  const b = overlay ?? emptyAssistantSchedule();
  const aClosed = assistantScheduleClosedWeekdays(a);
  const bClosed = assistantScheduleClosedWeekdays(b);
  const closed_weekdays = aClosed.length > 0 ? aClosed : bClosed;
  const closedSet = new Set(closed_weekdays);
  const openWeekdays = (a.weekdays.length > 0 ? a.weekdays : b.weekdays).filter(
    (day) => !closedSet.has(day),
  );
  const merged: AssistantSchedule = {
    frequency: a.frequency ?? b.frequency,
    weekdays: openWeekdays,
    closed_weekdays,
    start_time: a.start_time ?? b.start_time,
    duration_minutes: a.duration_minutes ?? b.duration_minutes,
    until_date: a.until_date ?? b.until_date,
  };
  return isAssistantScheduleActive(merged) ? merged : null;
}
